/**
 * WhatsApp вне окна 24 ч (спека docs/specs/2026-11-03-wa-closed-window-ux-design.md, вариант A).
 *
 * Вне окна каждое сообщение уходит отдельным платным «Универсальным шаблоном 4» (MARKETING), и
 * WhatsApp отклоняет шаблоны подряд клиенту, который не отвечает: Wazzup приносит `UNKNOWN_ERROR`
 * (docs/knowledge/whatsapp-shablon-vne-okna.md). Поэтому над полем — одна строка по ступеням, а у
 * отклонённого сообщения — причина по-русски. Отправку не блокируем.
 *
 * - `waStage` — ступень по сообщениям этого чата: а) наших шаблонов за сутки после клиента нет;
 *   б) уже уходили; в) был отказ `UNKNOWN_ERROR`; отдельно — `BAD_CONTACT` (номера нет в WhatsApp).
 *   Считаем только сутки: на этом окне и замечен отказ (первый шаблон доходит почти всегда).
 * - `waErrorText` — строка под отклонённым сообщением.
 * - `WaWindowNote` — строка над полем, «Подробнее» раскрывает объяснение и чаты клиента в других каналах.
 */
import { ChevronDown, Copy } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router";

import type { Chat, ChatBrief, ChatMessage } from "@/lib/clientChats";

export type WaStage = "first" | "repeat" | "blocked" | "contact";

const DAY = 24 * 60 * 60 * 1000;
const isUnknown = (e: string | null | undefined) => (e ?? "").startsWith("UNKNOWN_ERROR");
const isBadContact = (e: string | null | undefined) => (e ?? "").startsWith("BAD_CONTACT");

/** Ступень предупреждения; `null` — окно открыто или это не WhatsApp. `messages` — лента (может
 * быть общей лентой клиента: берём только сообщения этого чата). */
export function waStage(chat: Chat, messages: ChatMessage[], now: number = Date.now()): WaStage | null {
  if (!chat.whatsapp || chat.whatsapp.open) return null;
  const lastIn = chat.whatsapp.last_in ? Date.parse(chat.whatsapp.last_in) : 0;
  const since = Math.max(lastIn, now - DAY);
  const ours = messages.filter((m) => m.chat_id === chat.id && m.out && m.id > 0 && Date.parse(m.date) >= since);
  if (ours.some((m) => m.status === "error" && isUnknown(m.error))) return "blocked";
  if (ours.some((m) => m.status === "error" && isBadContact(m.error))) return "contact";
  if (ours.some((m) => m.status !== "error" && m.status !== "sending")) return "repeat";
  return "first";
}

/** Причина отказа доставки по-русски; `null` — показывать нечего. */
export function waErrorText(m: ChatMessage): string | null {
  if (!m.out || m.status !== "error" || m.id < 0) return null;
  if (isUnknown(m.error))
    return "Не доставлено: WhatsApp не принял сообщение. Так бывает, когда клиент не ответил на прошлые, — повторять не стоит.";
  if (isBadContact(m.error)) return "Не доставлено: номера нет в WhatsApp или у клиента старая версия. Уточните номер.";
  return `Не доставлено: ${m.error || "причина не передана"}`;
}

/** Строка под отклонённым сообщением: причина и «Скопировать текст» — отправить в другом канале. */
export function WaError({ m }: { m: ChatMessage }) {
  const [copied, setCopied] = useState<"" | "ok" | "fail">("");
  const text = waErrorText(m);
  if (!text) return null;
  const copy = () => {
    if (!navigator.clipboard) return setCopied("fail");
    void navigator.clipboard.writeText(m.text).then(
      () => setCopied("ok"),
      () => setCopied("fail"),
    );
  };
  return (
    <div
      className="clear-both mt-1.5 rounded-lg bg-[#e53935] px-2 py-1.5 text-[13px] leading-[18px] text-white"
      data-annot="причина отказа WhatsApp"
    >
      <span className="font-medium">{text}</span>
      {m.text && (
        <span className="mt-1 flex items-center gap-2">
          <button
            type="button"
            onClick={copy}
            className="flex items-center gap-1 font-medium underline decoration-dotted underline-offset-2 hover:no-underline"
            title="Скопировать текст сообщения, чтобы отправить его в другом канале"
          >
            <Copy size={13} /> Скопировать текст
          </button>
          <span role="status" aria-live="polite" className="text-[12px]">
            {copied === "ok" ? "скопировано — вставьте в другом канале" : copied === "fail" ? "не вышло — выделите текст вручную" : ""}
          </span>
        </span>
      )}
    </div>
  );
}

const NOTE: Record<WaStage, { tone: string; short: string; long: string }> = {
  first: {
    tone: "text-[var(--tg-sec)]",
    short: "Окно 24 ч WhatsApp закрыто — пишите всё одним сообщением",
    long: "Каждое сообщение уходит отдельным платным шаблоном одной строкой (до 550 знаков), файлы не отправить. Несколько сообщений подряд WhatsApp может не принять — соберите всё важное в одно.",
  },
  repeat: {
    tone: "text-[#b26a00]",
    short: "Уже отправляли без ответа — следующее WhatsApp может не принять",
    long: "Окно 24 ч закрыто: каждое сообщение — отдельный платный шаблон. Клиенту, который не отвечает, WhatsApp часто не принимает второй и следующие. Соберите всё важное в одно сообщение или напишите в другой канал.",
  },
  blocked: {
    tone: "text-[#e53935]",
    short: "WhatsApp отклоняет сообщения — напишите в другой канал или позвоните",
    long: "WhatsApp не принял наше сообщение: клиент не ответил на прошлые. Повторять не стоит — снова не примет. Сообщения пойдут, когда клиент напишет сам; пока — другой канал или звонок.",
  },
  contact: {
    tone: "text-[#e53935]",
    short: "Номера нет в WhatsApp — уточните номер",
    long: "WhatsApp ответил, что номера нет в мессенджере или у клиента старая версия. Уточните номер; пока свяжитесь в другом канале или позвоните.",
  },
};

/** Строка над полем ответа WhatsApp при закрытом окне; «Подробнее» — объяснение и другие чаты клиента. */
export function WaWindowNote({
  chat,
  stage,
  place,
  onChat,
}: {
  chat: Chat;
  stage: WaStage;
  /** Подпись чата «где общались», как в переключателе чатов. */
  place: (c: ChatBrief) => string;
  onChat?: ((id: number) => void) | undefined;
}) {
  const [open, setOpen] = useState(false);
  const { search } = useLocation();
  const note = NOTE[stage];
  const others = chat.related.filter((c) => c.channel !== "whatsapp");
  const lastIn = chat.whatsapp?.last_in;
  return (
    <div className={`mb-1 px-3 text-[13px] ${note.tone}`} data-annot="окно 24 ч WhatsApp">
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1" role="status" aria-live="polite">
          {note.short}
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={`wa-note-${chat.id}`}
          className="flex flex-none items-center gap-0.5 underline decoration-dotted underline-offset-2 hover:no-underline"
        >
          {open ? "Скрыть" : "Подробнее"}
          <ChevronDown size={14} className={open ? "rotate-180" : ""} />
        </button>
      </div>
      {open && (
        <div id={`wa-note-${chat.id}`} className="mt-1 text-[var(--tg-sec)]">
          <p>
            {lastIn ? "" : "Клиент в WhatsApp ещё не писал. "}
            {note.long}
          </p>
          {others.length > 0 && (
            <p className="mt-1 flex flex-wrap items-center gap-1.5">
              <span>Другие чаты клиента:</span>
              {others.map((c) => (
                <Link
                  key={c.id}
                  to={{ pathname: `/chats/${c.id}`, search }}
                  onClick={(e) => {
                    if (onChat) {
                      e.preventDefault();
                      onChat(c.id);
                    }
                  }}
                  className="rounded-full bg-[var(--tg-surface)] px-2.5 py-0.5 font-medium text-[var(--tg-primary)] shadow-[var(--tg-shadow)] hover:bg-[var(--tg-hover)]"
                >
                  {place(c)}
                </Link>
              ))}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
